import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../supabase'

const SupabaseAuthContext = createContext({})

export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext)
  if (!context) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider')
  }
  return context
}

export const SupabaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      setUser(session?.user ?? null)
      setLoading(false)
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const checkUsernameAvailability = async (username) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', username)
        .single()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      // If data exists, username is taken
      return { available: !data, error: null }
    } catch (error) {
      return { available: false, error: error.message }
    }
  }

  const signUp = async (email, password, username) => {
    try {
      // First check if username is available
      const usernameCheck = await checkUsernameAvailability(username)
      if (!usernameCheck.available) {
        return { success: false, error: 'Username is already taken' }
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username: username
          }
        }
      })

      if (error) throw error

      // Automatically sign in the user after successful registration
      if (data.user) {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        })
        
        if (signInError) {
          console.warn('Could not auto-sign in after registration:', signInError)
          // Still return success even if auto-sign in fails
          return { success: true, data }
        }
        
        return { success: true, data: signInData }
      }

      return { success: true, data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const signInWithUsernameOrEmail = async (usernameOrEmail, password) => {
    try {
      // First try to find the user by username
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('username', usernameOrEmail)
        .single()

      let email = usernameOrEmail

      // If we found a profile with this username, use that email
      if (profileData && !profileError) {
        email = profileData.email
      }

      // Now sign in with the email
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

      return { success: true, data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

      return { success: true, data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const updateProfile = async (updates) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: updates
      })

      if (error) throw error

      return { success: true, data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signInWithUsernameOrEmail,
    signOut,
    updateProfile,
    checkUsernameAvailability
  }

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  )
} 